import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

// Podpisové plátno. Vždy bílé s černým tahem bez ohledu na téma — podpis je
// doklad, zobrazuje se i na světlém pozadí a v tisku, bílý tah by v něm zmizel.
export const SignaturePad = forwardRef(function SignaturePad({ onChange }, ref) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawing = useRef(false);
  const emptyRef = useRef(true);
  const [empty, setEmpty] = useState(true);

  const markEmpty = (v) => { emptyRef.current = v; setEmpty(v); onChange?.(v); };

  const setup = () => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const w = wrap.clientWidth;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = 170 * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = "170px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, 170);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#000";
    markEmpty(true);
  };

  useEffect(() => {
    setup();
    // Při resize se obsah zahodí — plátno je zase prázdné.
    const onResize = () => setup();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    isEmpty: () => emptyRef.current,
    clear: setup,
    toBlob: () => new Promise((r) => canvasRef.current.toBlob(r, "image/png")),
  }));

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  };
  const down = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const [x, y] = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    // Prázdnost hlídá příznak, ne pixely — plátno je bílé, ne průhledné.
    if (emptyRef.current) markEmpty(false);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const [x, y] = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const up = () => { drawing.current = false; };

  return (
    <div ref={wrapRef} className="w-full min-w-0">
      <canvas ref={canvasRef} data-testid="signature-pad"
        style={{ touchAction: "none" }}
        onPointerDown={down} onPointerMove={move} onPointerUp={up}
        onPointerCancel={up} onPointerLeave={up}
        className={`w-full rounded-[14px] border-2 border-dashed bg-white cursor-crosshair ${
          empty ? "border-border" : "border-success"}`} />
      <div className="flex items-center justify-between mt-2">
        <span className="text-[13px] text-muted-foreground">
          {empty ? "Podepište se prstem nebo myší" : "Podepsáno"}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={setup} data-testid="signature-clear"
          className="text-muted-foreground hover:text-destructive h-8">Vymazat</Button>
      </div>
    </div>
  );
});
